import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { CommandPalette } from '../ui/index.ts';
import type { CommandGroup } from '../ui/index.ts';
import { visibleNavGroups } from './navigation.ts';
import { loadRecentPaths } from './recentPaths.ts';

/**
 * ⌘K palette wiring for the tenant Shell (epic #897 PR 2). Sources:
 *
 *   1. Request deep-link — a `req_…` query jumps straight into the
 *      request logs with the id prefilled (the page reads `?requestId=`
 *      from the URL). Only offered when the user can see /requests.
 *   2. Recent — the last visited nav destinations (localStorage),
 *      shown while the query is empty.
 *   3. Navigation — the user's permission-filtered nav items,
 *      substring-matched on label + path + group title.
 *
 * No account entity search — that is an ops-admin-only feature and has
 * no tenant-facing endpoint. Filtering is manual (`shouldFilter` off in
 * the primitive) so the request deep-link coexists with static items.
 */
interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function ShellCommandPalette({ open, onOpenChange }: Props): JSX.Element {
  const { user } = useSession();
  const navigate = useNavigate();
  const t = useT();
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  const normalized = query.trim().toLowerCase();

  const groups = useMemo<readonly CommandGroup[]>(() => {
    const out: CommandGroup[] = [];

    function go(path: string): void {
      onOpenChange(false);
      navigate(path);
    }

    const navGroups = visibleNavGroups(user);
    const allItems = navGroups.flatMap((group) => group.items);

    // Request deep-link — an exact-looking request id beats everything.
    const canSeeRequests = allItems.some((item) => item.to === '/requests');
    if (canSeeRequests && /^req_[a-z0-9]/i.test(query.trim())) {
      const requestId = query.trim();
      out.push({
        heading: t('shell.cmdk.group.requests'),
        items: [
          {
            id: `req:${requestId}`,
            label: t('shell.cmdk.open_request', { id: requestId }),
            hint: 'req',
            onSelect: () => go(`/requests?requestId=${encodeURIComponent(requestId)}`),
          },
        ],
      });
    }

    // Recent destinations (only when not searching).
    if (normalized.length === 0) {
      const recent = loadRecentPaths()
        .map((path) => allItems.find((item) => item.to === path))
        .filter((item): item is NonNullable<typeof item> => item !== undefined)
        .slice(0, 5);
      if (recent.length > 0) {
        out.push({
          heading: t('shell.cmdk.group.recent'),
          items: recent.map((item) => ({
            id: `recent:${item.to}`,
            label: t(item.labelKey),
            hint: item.to,
            onSelect: () => go(item.to),
          })),
        });
      }
    }

    // Navigation — manually filtered against label, path, and group.
    for (const group of navGroups) {
      const items = group.items.filter((item) => {
        if (normalized.length === 0) {
          return true;
        }
        const haystack = `${t(item.labelKey)} ${item.to} ${t(group.titleKey)}`.toLowerCase();
        return haystack.includes(normalized);
      });
      if (items.length > 0) {
        out.push({
          heading: t(group.titleKey),
          items: items.map((item) => ({
            id: `nav:${item.to}`,
            label: t(item.labelKey),
            hint: item.to,
            onSelect: () => go(item.to),
          })),
        });
      }
    }

    return out;
  }, [query, normalized, user, t, navigate, onOpenChange]);

  return (
    <CommandPalette
      open={open}
      onOpenChange={onOpenChange}
      groups={groups}
      search={query}
      onSearchChange={setQuery}
      placeholder={t('shell.cmdk.placeholder')}
      emptyText={t('shell.cmdk.empty')}
      ariaLabel={t('shell.cmdk.aria_label')}
      footer={t('shell.cmdk.footer_hint')}
      testId="shell-cmdk"
    />
  );
}
